import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, TrendingUp, DollarSign, Cpu, Users2, ArrowLeft,
  Activity, Percent, ArrowUpRight, LogOut, MousePointerClick, Sparkles, RotateCcw,
  Unlock, Rocket, Database, Zap, Chrome, CheckCircle2, AlertTriangle, XCircle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { ClientDetailDrawer } from "../components/ClientDetailDrawer";
import { EmailLogPanel } from "../components/admin/EmailLogPanel";
import { BrowserHealthChip } from "../components/admin/BrowserHealthChip";
import { CronJobsCard } from "../components/admin/CronJobsCard";
import DeepScanHealthBadge from "../components/DeepScanHealthBadge";
import ReachabilityCard from "../components/admin/ReachabilityCard";
import LeadPipelineCard from "../components/admin/LeadPipelineCard";
import ScannerHealthCard from "../components/admin/ScannerHealthCard";

const money = (n) => (typeof n === "number" ? `$${n.toFixed(2)}` : "—");
const cents = (n) => (typeof n === "number" ? `$${n.toFixed(4)}` : "—");
const pct = (n) => (typeof n === "number" ? `${n.toFixed(1)}%` : "—");
const num = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const dt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
};

export default function AdminCost() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [scanFunnel, setScanFunnel] = useState(null);
  const [completePlus, setCompletePlus] = useState(null);
  const [dbHealth, setDbHealth] = useState(null);
  const [aiCircuit, setAiCircuit] = useState(null);
  const [pwHealth, setPwHealth] = useState(null);
  const [cronJobs, setCronJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overview, f, sf, cp, dbh, aic, pwh, cj] = await Promise.all([
        api.get("/admin/cost-overview"),
        api.get("/admin/nudge-funnel").catch(() => ({ data: null })),
        api.get("/admin/free-scan-funnel").catch(() => ({ data: null })),
        api.get("/admin/complete-plus-eligible").catch(() => ({ data: null })),
        api.get("/admin/db-health").catch(() => ({ data: null })),
        api.get("/admin/ai-circuit").catch(() => ({ data: null })),
        api.get("/admin/playwright-health").catch(() => ({ data: null })),
        api.get("/admin/cron-jobs").catch(() => ({ data: null })),
      ]);
      setData(overview.data);
      setFunnel(f.data);
      setScanFunnel(sf.data);
      setCompletePlus(cp.data);
      setDbHealth(dbh.data);
      setAiCircuit(aic.data);
      setPwHealth(pwh.data);
      setCronJobs(cj.data?.jobs || null);
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail);
      setError(err.response?.status === 403 ? "Owner access only." : detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Live ticker: silently refetch every 30s so the margin number breathes without a page reload.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Track which rows are mid-reset so we can disable the button + show a spinner.
  const [resettingUserId, setResettingUserId] = useState(null);
  const [unstickingUserId, setUnstickingUserId] = useState(null);
  const [drawerUserId, setDrawerUserId] = useState(null);

  /** Owner action — clear a customer's rolling 30-day manual-scan counter.
      Backend reclassifies their scans (doesn't delete) so cost/telemetry stays intact. */
  const resetScans = useCallback(async (client) => {
    const label = client.organization_name || client.email;
    if (!window.confirm(
      `Reset the monthly manual-scan counter for ${label}?\n\n` +
      `They'll be able to run scans again immediately. Cost history is preserved.`
    )) return;
    setResettingUserId(client.user_id);
    try {
      const { data: resp } = await api.post("/admin/reset-manual-scans", { email: client.email });
      toast.success(resp.message || `Reset scans for ${label}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't reset scans");
    } finally {
      setResettingUserId(null);
    }
  }, [load]);

  /** Owner action — force-release a stuck `scanning` flag on a customer's account.
      Belt-and-suspenders for the 240s hard-timeout — if a scan somehow escapes it. */
  const unstickScan = useCallback(async (client) => {
    const label = client.organization_name || client.email;
    if (!window.confirm(
      `Force-clear the "scanning" state for ${label}?\n\n` +
      `Use this only if their scan appears stuck. Safe if they aren't actually scanning.`
    )) return;
    setUnstickingUserId(client.user_id);
    try {
      const { data: resp } = await api.post("/admin/unstick-scan", { email: client.email });
      toast.success(resp.message || `Unstuck ${label}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't unstick scan");
    } finally {
      setUnstickingUserId(null);
    }
  }, [load]);

  const isOwner = user?.is_owner === true;

  if (user && !isOwner) {
    return (
      <div className="min-h-screen grid place-items-center bg-charcoal-50 px-6">
        <div className="max-w-md w-full text-center bg-white rounded-2xl border border-black/5 shadow-soft p-10" data-testid="not-owner-block">
          <div className="w-12 h-12 rounded-xl bg-charcoal-900 grid place-items-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-semibold text-charcoal-900 tracking-tight">Owner access only</h1>
          <p className="text-sm text-charcoal-500 mt-2">This dashboard is only available to the Mission Watch owner account.</p>
          <button onClick={() => navigate("/dashboard")}
            className="mt-6 inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-charcoal-900 text-white hover:bg-charcoal-800 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const clients = data?.clients || [];
  const marginPct = s?.mrr ? (s.gross_margin_monthly / s.mrr) * 100 : null;

  return (
    <div className="min-h-screen bg-charcoal-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900 text-[15px]">Mission Watch</span>
            <span className="ml-2 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-charcoal-900 text-emerald-400">Owner</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-sm text-charcoal-500 hover:text-charcoal-900 hidden sm:inline">Customer dashboard</Link>
            <button onClick={load} disabled={loading}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-black/10 text-charcoal-700 hover:bg-charcoal-50 transition-colors disabled:opacity-60"
              data-testid="cost-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-cost-page">
        <div className="mb-8">
          <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Owner · Cost dashboard</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-charcoal-900 mt-1">Revenue, costs &amp; margins</h1>
          <p className="text-sm text-charcoal-500 mt-2 max-w-2xl">Live view of MRR, AI spend and per-client unit economics. All figures are for the last 30 days unless noted.</p>
        </div>

        {loading && !data && (
          <div className="text-center py-16 text-sm text-charcoal-400 font-mono" data-testid="cost-loading">Loading cost overview…</div>
        )}
        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-100 px-5 py-4 text-sm text-red-800" data-testid="cost-error">{error}</div>
        )}

        {s && (
          <>
            {/* Live margin ticker — the answer to "am I making money" in one glance.
                Auto-refreshes every 30s. Colour-coded against the 90% profit floor target
                you locked in during v2 phase 6. Owner asks this constantly, so it's now
                answered before they scroll to any other metric. */}
            {/* Email delivery health banner — surfaces outbound email
                delivery issues (welcomes, alerts, monthly reports, password
                resets) so the owner sees them without having to scroll into
                the email history. Green when 0 failed in 24h; amber for
                1-3 failed; red for 4+ or ≥30% failure ratio. */}
            {(() => {
              const eh = s.email_health_24h;
              if (!eh) return null;
              const tone = eh.status || "green";
              const styles = {
                green: {
                  ring: "border-emerald-200 bg-emerald-50/70",
                  dot: "bg-emerald-500",
                  label: "text-emerald-800",
                  body: "text-emerald-900/80",
                },
                amber: {
                  ring: "border-amber-200 bg-amber-50/70",
                  dot: "bg-amber-500",
                  label: "text-amber-800",
                  body: "text-amber-900/80",
                },
                red: {
                  ring: "border-red-200 bg-red-50/70",
                  dot: "bg-red-500",
                  label: "text-red-800",
                  body: "text-red-900/80",
                },
              }[tone];
              const headline =
                tone === "green"
                  ? "All emails delivering normally"
                  : tone === "amber"
                  ? `${eh.failed} failed deliver${eh.failed === 1 ? "y" : "ies"} need attention`
                  : "Email Delivery Issues";
              const body =
                tone === "green"
                  ? `${eh.sent} sent · 0 failed in the last 24h`
                  : `${eh.failed} failed · ${eh.sent} sent · last 24h — open the email history for details`;
              return (
                <div
                  className={`mt-4 rounded-2xl border p-4 flex items-center justify-between gap-3 ${styles.ring}`}
                  data-testid="email-delivery-banner"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`inline-block w-2 h-2 rounded-full ${styles.dot}`} />
                    <div className="min-w-0">
                      <p className={`text-[10px] font-mono uppercase tracking-widest ${styles.label}`}>
                        Email Delivery · last 24h
                      </p>
                      <p className={`mt-0.5 text-sm font-medium ${styles.label}`} data-testid="email-delivery-headline">
                        {headline}
                      </p>
                      <p className={`mt-0.5 text-[12px] ${styles.body}`}>{body}</p>
                    </div>
                  </div>
                  {tone !== "green" && (
                    <a
                      href="#email-log"
                      onClick={(e) => {
                        // Iter-56d — smooth-scroll fallback for the
                        // "View log" jump. Native anchor works too, but
                        // this centers the panel and doesn't clobber
                        // the URL history stack.
                        const el = document.getElementById("email-log");
                        if (el) {
                          e.preventDefault();
                          el.scrollIntoView({behavior: "smooth", block: "start"});
                        }
                      }}
                      className={`shrink-0 text-[11px] font-mono uppercase tracking-wider ${styles.label} hover:underline`}
                      data-testid="email-delivery-jump"
                    >
                      View log →
                    </a>
                  )}
                </div>
              );
            })()}

            <MarginTicker
              mrr={s.mrr}
              margin={s.gross_margin_monthly}
              marginPct={marginPct}
              payingClients={s.paying_clients ?? 0}
              trialingClients={s.trialing_clients ?? 0}
            />

            {/* Iter-56b — Command Center moved directly under the MarginTicker.
                It's the owner's primary daily workflow (Prospect Scanner → Leads
                → Customers), so it needs to be immediately accessible without
                scrolling past every operational card first. */}
            <div className="mt-6" data-testid="command-center-top-slot">
              <LeadPipelineCard />
            </div>

            {/* AI health banner — silent-fallback detector.
                If the LLM key rotates, expires, or the provider is down, every scan
                falls back to a deterministic template summary and this ratio spikes.
                Only rendered when there's actual signal (>0). Zero fallbacks = hidden. */}
            {s.ai_events_24h > 0 && s.ai_fallbacks_24h > 0 && (
              <div
                className={
                  "mt-4 rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 " +
                  (s.ai_fallback_ratio_24h >= 0.5
                    ? "border-red-200 bg-red-50/60"
                    : "border-amber-200 bg-amber-50/60")
                }
                data-testid="ai-health-banner"
              >
                <div className="min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
                    AI health · last 24h
                  </p>
                  <p
                    className={
                      "mt-1 text-sm font-medium " +
                      (s.ai_fallback_ratio_24h >= 0.5 ? "text-red-900" : "text-amber-900")
                    }
                  >
                    {s.ai_fallbacks_24h} of {s.ai_events_24h} reports fell back to the template summary
                    <span className="font-mono text-charcoal-500 ml-1">
                      ({(s.ai_fallback_ratio_24h * 100).toFixed(0)}%)
                    </span>
                  </p>
                  <p className="mt-0.5 text-[13px] text-charcoal-600 leading-snug">
                    Customer reports are still generating, but without live GPT-5.2 polish.
                    Usually a rotated/expired <span className="font-mono">EMERGENT_LLM_KEY</span> — check your Emergent Universal Key balance or rotate the key in production Secrets.
                  </p>
                </div>
              </div>
            )}

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-cards">
              <Kpi icon={DollarSign} label="MRR (paying)" value={money(s.mrr)} sub={`${num(s.paying_clients ?? 0)} paying clients`} accent="emerald" testId="kpi-mrr" />
              <Kpi icon={Cpu} label="AI cost (30d)" value={money(s.ai_cost_30d)} sub={`${num(s.reports_30d)} reports`} accent="amber" testId="kpi-ai-cost" />
              <Kpi icon={TrendingUp} label="Gross margin" value={money(s.gross_margin_monthly)} sub={marginPct != null ? pct(marginPct) : "—"} accent="brand" testId="kpi-margin" />
              <Kpi icon={Percent} label="Stripe fees" value={money(s.stripe_fees_monthly)} sub={`${(s.rates.stripe_pct * 100).toFixed(1)}% + $${s.rates.stripe_fixed.toFixed(2)}`} accent="charcoal" testId="kpi-stripe" />
            </div>

            {/* second row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <MiniCard
                label="Trial pipeline"
                value={money(s.trial_pipeline_mrr ?? 0)}
                sub={s.trialing_clients ? `${num(s.trialing_clients)} trial${s.trialing_clients === 1 ? "" : "s"} converting soon` : "No active trials"}
                testId="mini-trial-pipeline"
              />
              <MiniCard label="Checkup clients" value={num(s.essential_clients)} sub="$19 / mo · paying" />
              <MiniCard label="Protection clients" value={num(s.complete_clients)} sub="$39 / mo · paying" />
              <MiniCard label="Avg cost / report" value={cents(s.avg_cost_per_report)} sub={`${num(s.avg_tokens_per_report)} tokens avg`} />
            </div>

            {/* rates */}
            <div className="mt-4 rounded-2xl bg-white border border-black/5 shadow-soft p-5 flex flex-wrap gap-x-6 gap-y-2 items-center" data-testid="rates-strip">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-charcoal-400" />
                <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-400">Rate assumptions</span>
              </div>
              <span className="text-xs text-charcoal-600">AI input <span className="font-mono">${s.rates.ai_input_per_1m}/1M tokens</span></span>
              <span className="text-xs text-charcoal-600">AI output <span className="font-mono">${s.rates.ai_output_per_1m}/1M tokens</span></span>
              <span className="text-xs text-charcoal-600">Stripe <span className="font-mono">{(s.rates.stripe_pct * 100).toFixed(1)}% + ${s.rates.stripe_fixed.toFixed(2)}</span></span>
              <span className="ml-auto text-[11px] font-mono text-charcoal-400">Tune via env: AI_COST_INPUT_PER_1M / AI_COST_OUTPUT_PER_1M</span>
              <BrowserHealthChip />
            </div>

            {/* Upgrade nudge funnel */}
            {funnel && (
              <section className="mt-8 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden" data-testid="nudge-funnel-card">
                <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Upgrade nudge · last 30 days</p>
                    <h2 className="text-lg font-medium text-charcoal-900 mt-0.5 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-brand" /> Checkup → Protection conversion funnel
                    </h2>
                  </div>
                  <p className="hidden sm:block text-xs font-mono text-charcoal-400">
                    Attribution window: 72h from click
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-black/5">
                  <FunnelStep icon={Activity} label="Impressions"
                    value={funnel.impressions} sub={`${funnel.unique_users_impressed} unique users`}
                    testId="funnel-impressions" />
                  <FunnelStep icon={MousePointerClick} label="Clicks"
                    value={funnel.clicks} sub={funnel.click_through_rate != null ? `${funnel.click_through_rate}% CTR` : "—"}
                    testId="funnel-clicks" accent="brand" />
                  <FunnelStep icon={TrendingUp} label="Attributed upgrades"
                    value={funnel.attributed_upgrades} sub={funnel.end_to_end_rate != null ? `${funnel.end_to_end_rate}% end-to-end` : "—"}
                    testId="funnel-upgrades" accent="emerald" />
                </div>
                <div className="px-6 py-3 bg-charcoal-50/60 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] font-mono text-charcoal-500">
                  <span>Dismisses: <span className="text-charcoal-700">{funnel.dismisses}</span></span>
                  <span>Click → upgrade: <span className="text-charcoal-700">{funnel.click_to_upgrade_rate != null ? `${funnel.click_to_upgrade_rate}%` : "—"}</span></span>
                  <span className="ml-auto text-charcoal-400">
                    Impressions = modal shown · Clicks = CTA tapped · Upgrades = Stripe transition to Protection within 72h
                  </span>
                </div>
              </section>
            )}

            {/* Free-scan funnel — the top-of-funnel companion to the nudge funnel */}
            {scanFunnel && (
              <section className="mt-4 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden" data-testid="free-scan-funnel-card">
                <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Free scan · last 7 days</p>
                    <h2 className="text-lg font-medium text-charcoal-900 mt-0.5 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-brand" /> Landing → free scan → trial pipeline
                    </h2>
                  </div>
                  <p className="hidden sm:block text-xs font-mono text-charcoal-400">
                    From /free-scan requests
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-black/5">
                  <FunnelStep icon={Activity} label="Free scans requested"
                    value={scanFunnel.leads} sub={scanFunnel.window_days ? `In the last ${scanFunnel.window_days} days` : "—"}
                    testId="scan-funnel-leads" />
                  <FunnelStep icon={MousePointerClick} label="Registered"
                    value={scanFunnel.registered}
                    sub={scanFunnel.reg_rate_pct != null ? `${scanFunnel.reg_rate_pct}% of scans` : "—"}
                    testId="scan-funnel-registered" accent="brand" />
                  <FunnelStep icon={TrendingUp} label="Started trial or paid"
                    value={scanFunnel.converted}
                    sub={scanFunnel.trial_rate_pct != null ? `${scanFunnel.trial_rate_pct}% scan→trial` : "—"}
                    testId="scan-funnel-converted" accent="emerald" />
                </div>
                <div className="px-6 py-3 bg-charcoal-50/60 flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px] font-mono text-charcoal-500">
                  <span>Register → trial conversion: <span className="text-charcoal-700">{scanFunnel.conv_rate_from_registered_pct != null ? `${scanFunnel.conv_rate_from_registered_pct}%` : "—"}</span></span>
                  <span className="ml-auto text-charcoal-400">
                    Trial = 72h follow-up email is turning cold prospects into warm signups
                  </span>
                </div>
              </section>
            )}

            {/* Complete+ auto-bump — customers whose site crossed 250 pages */}
            {completePlus && (completePlus.notified?.length > 0 || completePlus.in_progress?.length > 0) && (
              <section className="mt-4 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden" data-testid="complete-plus-card">
                <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Complete+ auto-bump</p>
                    <h2 className="text-lg font-medium text-charcoal-900 mt-0.5 flex items-center gap-2">
                      <Rocket className="w-4 h-4 text-brand" /> Accounts crossing the {completePlus.page_threshold}-page threshold
                    </h2>
                  </div>
                  <p className="hidden sm:block text-xs font-mono text-charcoal-400">
                    Fires after {completePlus.streak_threshold} consecutive deep scans
                  </p>
                </div>
                {completePlus.notified?.length > 0 && (
                  <div className="px-6 py-4 border-b border-black/5">
                    <p className="text-[11px] font-mono uppercase tracking-[0.12em] text-emerald-600 mb-2">
                      Notified · {completePlus.notified.length} account{completePlus.notified.length === 1 ? "" : "s"}
                    </p>
                    <ul className="space-y-2" data-testid="complete-plus-notified">
                      {completePlus.notified.map((row) => (
                        <li key={row.user_id} className="flex items-start justify-between gap-4 text-sm py-1">
                          <div className="min-w-0">
                            <p className="text-charcoal-900 font-medium truncate">{row.organization_name || row.email}</p>
                            <p className="text-xs text-charcoal-400 font-mono truncate">
                              {row.email} · {row.notified_pages} pages · sent {dt(row.notified_at)}
                            </p>
                          </div>
                          <span className="text-[11px] font-mono uppercase tracking-wide px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 shrink-0">
                            emailed
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {completePlus.in_progress?.length > 0 && (
                  <div className="px-6 py-4">
                    <p className="text-[11px] font-mono uppercase tracking-[0.12em] text-amber-600 mb-2">
                      Building streak · {completePlus.in_progress.length} account{completePlus.in_progress.length === 1 ? "" : "s"}
                    </p>
                    <ul className="space-y-2" data-testid="complete-plus-in-progress">
                      {completePlus.in_progress.map((row) => (
                        <li key={row.user_id} className="flex items-start justify-between gap-4 text-sm py-1">
                          <div className="min-w-0">
                            <p className="text-charcoal-900 font-medium truncate">{row.organization_name || row.email}</p>
                            <p className="text-xs text-charcoal-400 font-mono truncate">
                              {row.email} · {row.last_pages} pages · last seen {dt(row.last_seen_at)}
                            </p>
                          </div>
                          <span className="text-[11px] font-mono uppercase tracking-wide px-2 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-100 shrink-0">
                            {row.streak}/{completePlus.streak_threshold}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {/* Ops health strip — DB / AI / Playwright observability */}
            {(dbHealth || aiCircuit || pwHealth) && (
              <section className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ops-health-strip">
                {/* AI Circuit */}
                {aiCircuit && (() => {
                  const state = aiCircuit.state; // closed / half_open / open
                  const pillCls = state === "closed"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : state === "half_open"
                    ? "bg-amber-50 text-amber-700 border-amber-100"
                    : "bg-red-50 text-red-700 border-red-100";
                  const Icon = state === "closed" ? CheckCircle2 : state === "half_open" ? AlertTriangle : XCircle;
                  return (
                    <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5" data-testid="ai-circuit-card">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-charcoal-500" />
                        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">AI provider</p>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <p className="text-lg font-medium text-charcoal-900">OpenAI</p>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wide px-2 py-1 rounded-full border ${pillCls}`}>
                          <Icon className="w-3 h-3" /> {state.replace("_", "-")}
                        </span>
                      </div>
                      <p className="text-xs text-charcoal-500 font-mono mt-1">
                        {aiCircuit.consecutive_failures}/{aiCircuit.failure_threshold} consecutive failures
                        {state === "open" && aiCircuit.cooldown_seconds_remaining > 0 && (
                          <> · retry in {aiCircuit.cooldown_seconds_remaining}s</>
                        )}
                      </p>
                      {aiCircuit.total_opens > 0 && (
                        <p className="text-[10px] text-charcoal-400 font-mono mt-1">
                          lifetime: {aiCircuit.total_opens} open{aiCircuit.total_opens === 1 ? "" : "s"} · {aiCircuit.total_blocked_calls} blocked
                        </p>
                      )}
                    </div>
                  );
                })()}
                {/* DB Health */}
                {dbHealth && (() => {
                  const dm = dbHealth.daily_metrics;
                  const fresh = dm?.is_fresh;
                  const stalePill = fresh
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : "bg-amber-50 text-amber-700 border-amber-100";
                  return (
                    <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5" data-testid="db-health-card">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-charcoal-500" />
                        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">DB health</p>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <p className="text-lg font-medium text-charcoal-900">
                          {dm?.total ?? 0} <span className="text-sm text-charcoal-400 font-mono">rollups</span>
                        </p>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wide px-2 py-1 rounded-full border ${stalePill}`}>
                          {fresh ? "fresh" : "stale"}
                        </span>
                      </div>
                      <p className="text-xs text-charcoal-500 font-mono mt-1">
                        {dm?.freshness_hours != null ? `${dm.freshness_hours}h since last rollup` : "no rollups yet"}
                      </p>
                      <p className="text-[10px] text-charcoal-400 font-mono mt-1">
                        health_reports: {dbHealth.health_reports?.deep ?? 0} deep · {dbHealth.health_reports?.with_ttl ?? 0} TTL'd · {dbHealth.health_reports?.kept_forever ?? 0} kept
                      </p>
                    </div>
                  );
                })()}
                {/* Playwright health */}
                {pwHealth && (() => {
                  const total = pwHealth.attempts;
                  const rate = pwHealth.success_rate_pct;
                  const pillCls = total === 0
                    ? "bg-charcoal-50 text-charcoal-500 border-charcoal-100"
                    : rate >= 95
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : rate >= 70
                    ? "bg-amber-50 text-amber-700 border-amber-100"
                    : "bg-red-50 text-red-700 border-red-100";
                  return (
                    <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5" data-testid="playwright-health-card">
                      <div className="flex items-center gap-2">
                        <Chrome className="w-4 h-4 text-charcoal-500" />
                        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Chromium (bot-solver)</p>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <p className="text-lg font-medium text-charcoal-900">
                          {total === 0 ? "idle" : `${rate}%`}
                        </p>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wide px-2 py-1 rounded-full border ${pillCls}`}>
                          {total === 0 ? "no launches" : total < 5 ? "warming up" : "monitored"}
                        </span>
                      </div>
                      <p className="text-xs text-charcoal-500 font-mono mt-1">
                        {pwHealth.successes} ok · {pwHealth.failures} failed · {total} total
                      </p>
                      {pwHealth.last_error && (
                        <p className="text-[10px] text-red-500 font-mono mt-1 truncate" title={pwHealth.last_error}>
                          last error: {pwHealth.last_error}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </section>
            )}

            {/* Cron jobs — external scheduler observability */}
            <CronJobsCard jobs={cronJobs} />

            {/* Iter-45b — Scanner health (owner-only operational card).
                Answers "is the monitoring system itself healthy?" in one
                glance. Sits right under CronJobs so all system-health
                signals are grouped. */}
            <ScannerHealthCard />

            {/* Iter-37b: Deep Scan Health — 24h failure badge. Small,
                admin-only, expandable details panel. Reuses existing
                admin endpoint pattern (owner-gated GET). */}
            <DeepScanHealthBadge />

            {/* Iter-56b — Lead pipeline moved to the top slot (directly under
                MarginTicker). This block previously lived here; kept the
                comment for context but the card itself now renders above. */}

            {/* Phase-1 reachability telemetry — internal, owner-only card.
                Measures how often our cluster IPs can reach nonprofit sites
                before we invest in Tier 2 proxy rotation. */}
            <div className="mt-6">
              <ReachabilityCard />
            </div>

            {/* clients table */}
            <section className="mt-8 rounded-2xl bg-white border border-black/5 shadow-soft overflow-hidden" data-testid="clients-table-card">
              <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Clients</p>
                  <h2 className="text-lg font-medium text-charcoal-900 mt-0.5 flex items-center gap-2">
                    <Users2 className="w-4 h-4 text-brand" /> Per-client unit economics
                  </h2>
                </div>
                <p className="text-xs font-mono text-charcoal-400">Sorted by AI cost · last 30 days</p>
              </div>

              {clients.length === 0 ? (
                <div className="text-center py-14 text-sm text-charcoal-400 font-mono" data-testid="clients-empty">
                  No active clients yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm" data-testid="clients-table">
                    <thead className="bg-charcoal-50/70">
                      <tr className="text-left text-[11px] font-mono uppercase tracking-[0.12em] text-charcoal-400">
                        <Th>Organization</Th>
                        <Th>Plan</Th>
                        <Th align="right">Pages</Th>
                        <Th align="right">Score</Th>
                        <Th align="right">Revenue</Th>
                        <Th align="right">Stripe fee</Th>
                        <Th align="right">Reports (30d)</Th>
                        <Th align="right">Scans used</Th>
                        <Th align="right">AI cost (30d)</Th>
                        <Th align="right">Cost / report</Th>
                        <Th align="right">Gross margin</Th>
                        <Th align="right">Last run</Th>
                        <Th align="right">Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((c) => {
                        const marginBad = c.gross_margin < 0;
                        const isResetting = resettingUserId === c.user_id;
                        return (
                          <tr key={c.user_id} className="border-t border-black/5 hover:bg-charcoal-50/40" data-testid="client-row">
                            <Td>
                              <button
                                onClick={() => setDrawerUserId(c.user_id)}
                                className="text-left hover:opacity-70 transition-opacity"
                                title="View full profile"
                                data-testid={`client-detail-btn-${c.user_id}`}
                              >
                                <div className="font-medium text-charcoal-900 hover:underline">{c.organization_name || "—"}</div>
                                <div className="text-[11px] font-mono text-charcoal-400 truncate max-w-[240px]">{c.email}</div>
                              </button>
                            </Td>
                            <Td>
                              <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full ${c.plan === "complete" ? "bg-brand/10 text-brand" : "bg-charcoal-100 text-charcoal-700"}`}>
                                {c.plan || "—"}{c.is_trialing ? " · trial" : ""}
                              </span>
                            </Td>
                            <Td align="right" mono>
                              {/* True total-page count (from monitoring_reach). Falls back to
                                  the last actually-scanned count for accounts that haven't
                                  yet had a successful deep sweep. */}
                              {c.total_pages ? (
                                <span
                                  className="text-charcoal-800"
                                  title={
                                    c.pages_scanned && c.pages_scanned !== c.total_pages
                                      ? `${c.pages_scanned} of ${c.total_pages} scanned this run (rotation window)`
                                      : "Total pages discovered on this site"
                                  }
                                >
                                  {num(c.total_pages)}
                                </span>
                              ) : (
                                <span className="text-charcoal-400">—</span>
                              )}
                            </Td>
                            <Td align="right" mono>
                              {c.latest_score != null ? (
                                <span
                                  className={
                                    c.latest_score >= 90
                                      ? "text-emerald-700 font-semibold"
                                      : c.latest_score >= 70
                                      ? "text-amber-700 font-semibold"
                                      : "text-red-700 font-semibold"
                                  }
                                  title={
                                    c.latest_scan_depth === "deep"
                                      ? "Latest deep-scan Mission Watch Score"
                                      : `Latest ${c.latest_scan_depth || "basic"}-scan score`
                                  }
                                >
                                  {c.latest_score}
                                </span>
                              ) : (
                                <span className="text-charcoal-400">—</span>
                              )}
                            </Td>
                            <Td align="right" mono>{money(c.revenue)}</Td>
                            <Td align="right" mono className="text-charcoal-500">{money(c.stripe_fee)}</Td>
                            <Td align="right" mono>{num(c.reports_30d)}</Td>
                            <Td align="right" mono>
                              {c.manual_scans_cap ? (
                                <span
                                  className={
                                    c.manual_scans_used >= c.manual_scans_cap
                                      ? "text-red-600 font-semibold"
                                      : c.manual_scans_used >= c.manual_scans_cap - 1
                                      ? "text-amber-600 font-semibold"
                                      : "text-charcoal-700"
                                  }
                                  title={
                                    c.manual_scans_used >= c.manual_scans_cap
                                      ? "Cap reached — customer can't run scans until reset or 30d rollover"
                                      : c.manual_scans_used >= c.manual_scans_cap - 1
                                      ? "One scan away from the monthly cap"
                                      : `${c.manual_scans_cap - c.manual_scans_used} scans left this month`
                                  }
                                >
                                  {c.manual_scans_used} / {c.manual_scans_cap}
                                </span>
                              ) : (
                                <span className="text-charcoal-400">—</span>
                              )}
                            </Td>
                            <Td align="right" mono>{money(c.ai_cost_30d)}</Td>
                            <Td align="right" mono className="text-charcoal-500">{cents(c.cost_per_report)}</Td>
                            <Td align="right" mono>
                              <span className={marginBad ? "text-red-600 font-semibold" : "text-emerald-700 font-semibold"}>
                                {money(c.gross_margin)}
                              </span>
                            </Td>
                            <Td align="right" mono className="text-charcoal-500 text-xs">{dt(c.last_run)}</Td>
                            <Td align="right">
                              <div className="flex items-center gap-2 justify-end">
                                <button
                                  onClick={() => unstickScan(c)}
                                  disabled={unstickingUserId === c.user_id}
                                  title="Force-clear a stuck 'scanning' state"
                                  data-testid={`unstick-scan-btn-${c.user_id}`}
                                  className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1.5 rounded-full border border-black/10 text-charcoal-600 hover:text-charcoal-900 hover:bg-charcoal-50 hover:border-charcoal-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <Unlock className={`w-3 h-3 ${unstickingUserId === c.user_id ? "animate-spin" : ""}`} strokeWidth={2.25} />
                                  {unstickingUserId === c.user_id ? "Unsticking…" : "Unstick"}
                                </button>
                                <button
                                  onClick={() => resetScans(c)}
                                  disabled={isResetting}
                                  title="Reset this customer's monthly manual-scan counter"
                                  data-testid={`reset-scans-btn-${c.user_id}`}
                                  className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1.5 rounded-full border border-black/10 text-charcoal-600 hover:text-charcoal-900 hover:bg-charcoal-50 hover:border-charcoal-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <RotateCcw className={`w-3 h-3 ${isResetting ? "animate-spin" : ""}`} strokeWidth={2.25} />
                                  {isResetting ? "Resetting…" : "Reset scans"}
                                </button>
                              </div>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <EmailLogPanel />

            <div className="mt-8 flex justify-between items-center text-sm">
              <Link to="/dashboard" className="text-charcoal-500 hover:text-charcoal-900 inline-flex items-center gap-1.5">
                <ArrowLeft className="w-4 h-4" /> Back to customer dashboard
              </Link>
              <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-brand hover:underline" data-testid="stripe-dashboard-link">
                Open Stripe Dashboard <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </>
        )}
      </main>
      <ClientDetailDrawer
        userId={drawerUserId}
        open={!!drawerUserId}
        onClose={() => setDrawerUserId(null)}
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent = "brand", testId }) {
  const accents = {
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    brand: "text-brand",
    charcoal: "text-charcoal-700",
  };
  return (
    <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-charcoal-400">
        <Icon className={`w-4 h-4 ${accents[accent]}`} />
        <span className="text-[11px] font-mono uppercase tracking-[0.14em]">{label}</span>
      </div>
      <p className="mt-2 text-3xl font-semibold tracking-tighter text-charcoal-900">{value}</p>
      {sub && <p className="mt-1 text-[11px] font-mono text-charcoal-400">{sub}</p>}
    </div>
  );
}

function MiniCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl bg-white/60 border border-black/5 p-4" data-testid={testId}>
      <p className="text-[11px] font-mono uppercase tracking-[0.12em] text-charcoal-400">{label}</p>
      <p className="mt-1 text-xl font-semibold text-charcoal-900 tracking-tight">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] font-mono text-charcoal-400">{sub}</p>}
    </div>
  );
}

// Live margin ticker sits at the top of the owner cost dashboard. Auto-refreshes with the
// parent every 30s. Colour-coded against the 90% target we locked in for the plan pricing.
//
// Beta-stage variant: while `payingClients === 0`, the hero renders a calm
// "Beta Status" summary instead of a big red negative-profit number. The
// negative margin math is technically accurate during pre-revenue but sends
// the wrong signal on the primary hero. Once ANY paying customer exists, the
// component auto-reverts to the full Margin/Profit view — no toggle needed.
const MARGIN_TARGET = 90;
function MarginTicker({ mrr, margin, marginPct, payingClients = 0, trialingClients = 0 }) {
  // ---- Beta-stage hero (pre-revenue) -----------------------------------
  if (!payingClients) {
    return (
      <div
        className="mb-6 rounded-2xl bg-charcoal-900 text-white p-6 sm:p-7 relative overflow-hidden ring-1 ring-white/10"
        data-testid="beta-status-hero"
      >
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-white/5 text-white/70 border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-white/40 dot-pulse" />
              <span className="text-[10px] font-mono uppercase tracking-[0.18em]">Live · refreshes every 30s</span>
            </span>
          </div>
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/50">Beta Status</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/40">Paying Organizations</p>
              <p className="mt-1 text-4xl font-semibold tabular-nums text-white" data-testid="beta-paying-count">0</p>
            </div>
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/40">Trial Organizations</p>
              <p className="mt-1 text-4xl font-semibold tabular-nums text-white" data-testid="beta-trial-count">
                {trialingClients}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/40">Monthly Revenue</p>
              <p className="mt-1 text-4xl font-semibold tabular-nums text-white" data-testid="beta-revenue">$0.00</p>
            </div>
          </div>
          <p className="mt-5 text-sm text-white/70" data-testid="beta-status-message">
            Waiting for first paying customer…
          </p>
        </div>
      </div>
    );
  }

  // ---- Standard revenue/margin hero (post-first paying customer) --------
  const noRevenue = !mrr;
  const pctVal = typeof marginPct === "number" ? marginPct : null;
  const status = noRevenue
    ? { label: "Awaiting first paying customer", tone: "neutral" }
    : pctVal >= MARGIN_TARGET
      ? { label: `On target · above the ${MARGIN_TARGET}% floor`, tone: "healthy" }
      : pctVal >= 80
        ? { label: `Below the ${MARGIN_TARGET}% target — watch AI + Stripe cost`, tone: "warn" }
        : { label: `Below floor — margin at risk`, tone: "risk" };
  const toneStyles = {
    healthy: { ring: "ring-emerald-500/30", pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", num: "text-emerald-400", bar: "bg-emerald-400" },
    warn: { ring: "ring-amber-500/30", pill: "bg-amber-500/15 text-amber-300 border-amber-500/30", num: "text-amber-400", bar: "bg-amber-400" },
    risk: { ring: "ring-red-500/30", pill: "bg-red-500/15 text-red-300 border-red-500/30", num: "text-red-400", bar: "bg-red-400" },
    neutral: { ring: "ring-white/10", pill: "bg-white/5 text-white/70 border-white/10", num: "text-white/70", bar: "bg-white/30" },
  };
  const t = toneStyles[status.tone];
  // Visual fill on the progress rail — capped at 100 for aesthetics.
  const railPct = Math.max(0, Math.min(100, pctVal || 0));

  return (
    <div className={`mb-6 rounded-2xl bg-charcoal-900 text-white p-6 sm:p-7 relative overflow-hidden ring-1 ${t.ring}`} data-testid="margin-ticker">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
      <div className="relative flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${t.pill}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${t.bar} dot-pulse`} />
              <span className="text-[10px] font-mono uppercase tracking-[0.18em]">Live · refreshes every 30s</span>
            </span>
          </div>
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/50">Gross margin</p>
          <div className="mt-1 flex items-baseline gap-3">
            <span className={`text-6xl sm:text-7xl font-semibold tracking-tighter tabular-nums ${t.num}`} data-testid="margin-ticker-pct">
              {pctVal != null ? `${pctVal.toFixed(1)}%` : "—"}
            </span>
            <span className="text-sm font-mono text-white/50">of ${mrr?.toFixed(0) ?? 0} MRR</span>
          </div>
          <p className="mt-2 text-sm text-white/70">{status.label}</p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1 min-w-[180px]">
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/50">Monthly profit</p>
          <p className={`text-3xl font-semibold tabular-nums ${t.num}`}>${typeof margin === "number" ? margin.toFixed(2) : "—"}</p>
          <p className="text-[11px] font-mono text-white/40">Target: ≥ {MARGIN_TARGET}% margin</p>
        </div>
      </div>
      {/* Progress rail vs 90% target */}
      <div className="relative mt-5 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className={`absolute left-0 top-0 h-full ${t.bar} transition-[width] duration-700`} style={{ width: `${railPct}%` }} />
        {/* Target line at 90% */}
        <div className="absolute top-1/2 -translate-y-1/2 w-px h-3 bg-white/40" style={{ left: `${MARGIN_TARGET}%` }} title={`${MARGIN_TARGET}% target`} />
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>;
}

function FunnelStep({ icon: Icon, label, value, sub, accent = "charcoal", testId }) {
  const accents = {
    brand: "text-brand bg-brand/10",
    emerald: "text-emerald-600 bg-emerald-100",
    charcoal: "text-charcoal-700 bg-charcoal-100",
  };
  return (
    <div className="px-6 py-6 flex items-start gap-4" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl shrink-0 grid place-items-center ${accents[accent]}`}>
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-400">{label}</p>
        <p className="mt-1 text-3xl font-semibold tracking-tighter text-charcoal-900 tabular-nums">{value ?? 0}</p>
        {sub && <p className="mt-0.5 text-[11px] font-mono text-charcoal-500">{sub}</p>}
      </div>
    </div>
  );
}

function Td({ children, align = "left", mono = false, className = "" }) {
  return (
    <td className={`px-4 py-3 align-top ${align === "right" ? "text-right" : "text-left"} ${mono ? "font-mono" : ""} ${className}`}>
      {children}
    </td>
  );
}
